import { Component, Input, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicantDataService } from 'src/app/services/applicant-data.service';
import { ToastrService } from 'ngx-toastr';
import { WizardPhase, WizardWorkflowState, DraftIncomeSource, StreamDecision, normalizeWizardState } from 'src/app/models/income-wizard-workflow';
import { ArgyleViewBagResponse } from 'src/app/models/Argyle';

@Component({
  selector: 'app-income-wizard',
  templateUrl: './income-wizard.component.html',
  styleUrls: ['./income-wizard.component.less'],
})
export class IncomeWizardComponent implements OnInit {
  // Set by parent when opening via NgbModal (componentInstance).
  @Input() screeningId!: number;
  @Input() argyleBag: ArgyleViewBagResponse | null = null;
  @Input() auditMode = false;

  readonly WizardPhase = WizardPhase;

  state: WizardWorkflowState | null = null;
  drafts: DraftIncomeSource[] = [];
  loading = true;
  advancing = false;

  // Review mode: temporarily show a single stream from overview for editing.
  reviewMode = false;
  reviewStreamIndex = 0;
  reviewDraftId: number | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private appData: ApplicantDataService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.screeningId == null || Number.isNaN(this.screeningId)) {
      this.loading = false;
      this.toast.error('Missing screening id for income wizard.');
      return;
    }
    // Load wizard state + drafts. Argyle bag is provided by the parent.
    this.appData.getWizardState(this.screeningId).subscribe({
      next: (s) => {
        if (s) {
          this.state = normalizeWizardState(s);
        } else {
          this.state = this.freshReadinessState();
        }
        // In audit mode, jump straight to overview regardless of wizard state.
        if (this.auditMode && this.state) {
          this.state.currentPhase = WizardPhase.Overview;
        }
        this.loadInitialDrafts();
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          this.state = this.freshReadinessState();
        } else {
          this.state = this.freshReadinessState();
        }
        this.loadInitialDrafts();
      },
    });
  }

  private loadInitialDrafts(): void {
    this.appData.getWizardDraftSources(this.screeningId).subscribe({
      next: (drafts) => {
        this.drafts = drafts ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  get phaseHeader(): string {
    if (this.auditMode) {
      return this.reviewMode ? 'Audit: Reviewing stream' : 'Income Audit Review';
    }
    if (!this.state) {
      return 'Income verification wizard';
    }
    const labels: Record<WizardPhase, string> = {
      [WizardPhase.Readiness]: 'Readiness check',
      [WizardPhase.Streams]: 'Review income streams',
      [WizardPhase.Overview]: 'Overview',
      [WizardPhase.Complete]: 'Complete',
    };
    const step = this.state.currentPhase + 1;
    const title = labels[this.state.currentPhase] ?? 'Income wizard';
    return `Step ${step} of 4: ${title}`;
  }

  onPhaseComplete(): void {
    if (!this.state || this.screeningId == null) {
      return;
    }
    if (this.state.currentPhase === WizardPhase.Complete) {
      return;
    }

    // Readiness (phase 0) → create the workflow and advance to Streams.
    if (this.state.currentPhase === WizardPhase.Readiness) {
      this.advancing = true;
      this.appData.startOrResumeWizard(this.screeningId).subscribe({
        next: () => {
          // Workflow created at phase 0 — now advance to phase 1 (Streams).
          this.appData.advanceWizardPhase(this.screeningId).subscribe({
            next: (s) => {
              this.state = normalizeWizardState(s);
              this.advancing = false;
            },
            error: () => {
              this.advancing = false;
              this.toast.error('Could not advance past readiness.');
            },
          });
        },
        error: () => {
          this.advancing = false;
          this.toast.error('Could not start the income wizard.');
        },
      });
      return;
    }

    // Overview (phase 2)
    // SQL only allows advance while CurrentPhase < 2; 
    // leaving overview uses finalize.
    if (this.state.currentPhase === WizardPhase.Overview) {
      this.advancing = true;
      this.appData.finalizeIncomeWizard(this.screeningId).subscribe({
        next: () => {
          this.advancing = false;
          this.activeModal.close('complete');
        },
        error: () => {
          this.advancing = false;
          this.toast.error('Could not finalize the income wizard.');
        },
      });
      return;
    }

    this.advancing = true;
    this.appData.advanceWizardPhase(this.screeningId).subscribe({
      next: (s) => {
        this.state = s;
        // Refresh drafts when advancing to overview so it has current data.
        this.refreshDrafts(() => { this.advancing = false; });
      },
      error: () => {
        this.advancing = false;
        this.toast.error('Could not advance to the next wizard step.');
      },
    });
  }

  onOverviewWorkflowStateChange(s: WizardWorkflowState): void {
    this.state = s;
  }

  /** Overview requests to review/edit a specific stream. */
  onEditStream(event: { streamIndex: number; draftId: number | null }): void {
    this.reviewMode = true;
    this.reviewStreamIndex = event.streamIndex;
    this.reviewDraftId = event.draftId;
  }

  /** Stream phase decision during review mode — save (or update) draft, return to overview. */
  onReviewDecision(decision: StreamDecision): void {
    if (!this.state || this.screeningId == null) {
      return;
    }
    this.advancing = true;

    this.appData.saveWizardDraftSource({
      ...decision.draftSource,
      draftId: this.reviewDraftId,
      wizardStateId: this.state.id,
      screeningId: this.screeningId,
      isAuditEdit: this.auditMode,
    }).subscribe({
      next: () => {
        this.reviewMode = false;
        this.reviewDraftId = null;
        this.refreshDrafts(() => { this.advancing = false; });
      },
      error: () => {
        this.advancing = false;
        this.toast.error('Could not save decision.');
      },
    });
  }

  /** Called by overview when a draft is removed. */
  onDraftRemoved(): void {
    this.refreshDrafts();
  }

  /** Cancel review — return to overview with no changes. */
  onReviewCancel(): void {
    this.reviewMode = false;
    this.reviewDraftId = null;
  }

  onWizardDone(): void {
    this.activeModal.close('complete');
  }

  /** Re-fetch drafts from the API and update the cached list. */
  refreshDrafts(onComplete?: () => void): void {
    this.appData.getWizardDraftSources(this.screeningId).subscribe({
      next: (drafts) => {
        this.drafts = drafts ?? [];
        onComplete?.();
      },
      error: () => {
        onComplete?.();
      },
    });
  }

  private freshReadinessState(): WizardWorkflowState {
    return {
      id: 0,
      screeningId: this.screeningId,
      currentPhase: WizardPhase.Readiness,
      streamIndex: 0,
      startedByUserId: 0,
      startedDate: '',
      lastUpdatedDate: '',
      overrodeReadiness: false,
      overrideUser: null,
      overrideDate: null,
      completedDate: null,
    };
  }
}
